import React, { useState } from "react";
import { Button, Tooltip, Upload, App } from "antd";
import {
  AudioOutlined,
  DeleteOutlined,
  LoadingOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import AudioPlayButton from "./AudioPlayButton";
import { customUpload } from "../../../utils";

interface Props {
  text: string;
  audioUrl?: string;
  onAudioChange: (url: string | undefined) => void;
  generateAudio?: (text: string) => Promise<string>;
  size?: "small" | "middle" | "large";
}

const TtsAudioControls: React.FC<Props> = ({
  text,
  audioUrl,
  onAudioChange,
  generateAudio,
  size = "small",
}) => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    if (!generateAudio) return;
    if (!text.trim()) {
      message.warning("Escribe el texto antes de generar el audio");
      return;
    }
    setLoading(true);
    try {
      const url = await generateAudio(text);
      onAudioChange(url);
      message.success("Audio generado");
    } catch (e: any) {
      console.error(e);
      message.error(`Error al generar audio: ${e?.message ?? "desconocido"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (options: any) => {
    setLoading(true);
    try {
      const { url } = await customUpload(options, "game-quiz/tts");
      onAudioChange(url);
      options.onSuccess?.({ url }, options.file);
      message.success("Audio subido");
    } catch (error) {
      console.error(error);
      message.error("Error al subir el audio");
      options.onError?.(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      {audioUrl && <AudioPlayButton src={audioUrl} size={size} />}
      {generateAudio && (
        <Tooltip title={audioUrl ? "Regenerar audio" : "Generar audio con TTS"}>
          <Button
            type="text"
            size={size}
            icon={loading ? <LoadingOutlined /> : <AudioOutlined />}
            onClick={handleGenerate}
            disabled={loading || !text.trim()}
            style={{ color: audioUrl ? "#722ed1" : undefined }}
          />
        </Tooltip>
      )}
      <Upload customRequest={handleUpload} showUploadList={false} accept="audio/*">
        <Tooltip title={audioUrl ? "Reemplazar con un archivo" : "Subir archivo de audio"}>
          <Button
            type="text"
            size={size}
            icon={<UploadOutlined />}
            disabled={loading}
          />
        </Tooltip>
      </Upload>
      {audioUrl && (
        <Button
          type="text"
          size={size}
          danger
          icon={<DeleteOutlined />}
          onClick={() => onAudioChange(undefined)}
        />
      )}
    </span>
  );
};

export default TtsAudioControls;
